import React from "react"
import {
  ArrayInput,
  BooleanInput,
  Edit,
  FormTab,
  SelectInput,
  TabbedForm,
  TextInput,
  SimpleFormIterator,
  FormDataConsumer,
  DateInput,
  NumberInput,
  useRefresh,
  useNotify,
} from "react-admin"
import INSURANCE_OPTIONS from "../../lib/constants/insurance-providers"
import { IconButton, makeStyles } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import { red } from "@material-ui/core/colors"
import ClaimDeleteBar from "../ClaimDeleteBar"
import TicketPanel from "../Claim/TicketPanel"
import { useForm } from "react-final-form"
import { LawFirmOptions } from "../../constants/lawFirms"

const insuranceChoices = [
  ...INSURANCE_OPTIONS.map(opt => ({ id: opt.value, name: opt.label })),
  { id: "other", name: "Other" },
]

const useStyles = makeStyles(theme => ({
  lastField: {
    marginBottom: theme.spacing(3),
  },
  deleteIcon: {
    color: red[500],
  },
  workflowTrigger: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}))

function Aside ({ record }: { record?: Claim }) {
  if (!record) {
    return null
  }

  return (
    <TicketPanel
      userId={record.userId}
      claimId={record.id}
      claim={record}
      type="bmwVsv"
    />
  )
}

function valueToBoolean (value: any) {
  if (typeof value === "string") {
    value = value.toLowerCase()
    if (value === "true" || value === "1" || value === "yes") {
      return true
    }
    if (value === "false" || value === "0" || value === "no") {
      return false
    }
  }

  if (value === true || value === 1) {
    return true
  }
  if (value === false || value === 0) {
    return false
  }
  else {
    throw new Error(
      `Value "${value}" can not be coerced to boolean`,
    )
  }
}

function WorkflowDeleteButton ({ className }: { className: string }) {
  const form = useForm()

  function handleClick (event: React.MouseEvent) {
    event.preventDefault()
    if (window.confirm("Are you sure you want to remove this value?")) {
      form.change("workflowTriggeredOn", null)
    }
  }

  return (
    <IconButton onClick={handleClick}>
      <DeleteIcon className={className} />
    </IconButton>
  )
}

export default function BmwVsvClaimEdit (
  props: Record<string, unknown>,
) {
  const classes = useStyles()
  const notify = useNotify()
  const refresh = useRefresh()

  return (
    <Edit
      {...props}
      aside={<Aside />}
      transform={data => {
        data.customFieldValues = data.customFieldValues || {}

        data.additionalFields?.forEach(
          (field: { id: string, value: any, datatype: string }) => {
            if (field.value === null || typeof field.value === "undefined") {
              return
            }

            let value: any
            if (field.datatype === "number") {
              value = Number(field.value)
            }
            else if (field.datatype === "boolean") {
              value = valueToBoolean(field.value)
            }
            else {
              value = field.value
            }
            data.customFieldValues[field.id] = { value }
          },
        )

        for (const key in data.customFieldValues) {
          if (data.customFieldValues[key]._value) {
            const dateObject = data.customFieldValues[key]
            let date: string
            if (dateObject.value._seconds) {
              date = new Date(dateObject.value._seconds * 1000)
                .toISOString()
                .split("T")[0]
            }
            else {
              date = dateObject.value
            }
            data.customFieldValues[key] = {
              value: date,
            }
          }
        }

        delete data.additionalFields

        if (data.poaUploaded && !data.poaUploadedAt) {
          data.poaUploadedAt = new Date()
        }

        if (!data.poaUploaded) {
          data.poaUploadedAt = undefined
        }

        if (data.poaUploadedAt === undefined) {
          data.poaUploadedAt = null
          data._poaUploadedAt = null
        }

        return data
      }}
    >
      <TabbedForm
        redirect="show"
        sanitizeEmptyValues={false}
        toolbar={<ClaimDeleteBar path="bmw-vsv-claims"/>}
      >
        <FormTab label="Summary">
          <BooleanInput
            source="lead"
            label="Lead"
          />
          <TextInput source="vsvId" label="VSV ID"/>
          <SelectInput
            source="lawFirm"
            allowEmpty={true}
            choices={LawFirmOptions}
            parse={(v: string) => v === "" ? null : v}
            emptyValue={""}
            emptyText="No law firm"
          />
          <div className={classes.workflowTrigger}>
            <DateInput
              source='workflowTriggeredOn'
              label='Workflow Executed On'
              locales='sv-SE'
              showTime
              disabled
            />
            <FormDataConsumer>
              {() => <WorkflowDeleteButton className={classes.deleteIcon} />}
            </FormDataConsumer>
          </div>
          <SelectInput
            source="claimActionStatus"
            choices={[
              { id: "active", name: "Active" },
              { id: "dead", name: "Dead" },
              { id: "below5k", name: "Below 5k"},
              { id: "onHold", name: "On Hold" },
              { id: "finished", name: "Finished" },
            ]}
          />
          <BooleanInput
            source="poaUploaded"
            label="POA Page Uploaded"
          />
          <TextInput source="notes" />
          <TextInput source="leadSource" />
        </FormTab>
        <FormTab label="Contact Info">
          <TextInput
            label="Claim First Name"
            source="firstName"
          />
          <TextInput
            label="Claim Last Name"
            source="lastName"
          />
          <TextInput
            label="Contract First Name"
            source="purchaseContractFirstName"
          />
          <TextInput
            label="Contract Last Name"
            source="purchaseContractLastName"
          />
          <TextInput
            label="Contract Company"
            source="purchaseContractCompanyName"
          />
          <TextInput source="address" />
          <TextInput source="city" />
          <TextInput source="postcode" />
          <TextInput source="phoneNumber" />
          <TextInput
            label="IBAN"
            source="iban"
            className={classes.lastField}
          />
        </FormTab>
        <FormTab label="BMW Cars">
          <SelectInput
            label="Car Type"
            source="bmwCarData.carType"
            choices={[
              { id: "bmw", name: "BMW" },
              { id: "mini", name: "MINI" },
            ]}
          />
          <SelectInput
            label="Vehicle Ownership"
            source="bmwCarData.ownership"
            choices={[
              { id: "owner", name: "Owner" },
              { id: "sold", name: "Sold" },
            ]}
          />
          <NumberInput
            label='Current Mileage'
            source='bmwCarData.currentMileage'
          />
          <NumberInput
            label='Mileage on Sales Contract'
            source='bmwCarData.salesMileage'
          />
        </FormTab>
        <FormTab label='Validation'>
          <TextInput label='Contract First Name'
            source='purchaseContractFirstName' />
          <TextInput label='Contract Last Name'
            source='purchaseContractLastName' />
          <TextInput source='address' />
          <TextInput source='city' />
          <TextInput source='postcode' />
          <TextInput source='phoneNumber' />

          <BooleanInput
            source='differentAddress'
            label='Different Residential Address'
          />

          <TextInput source='livingAddress' label="Residential Address"/>
          <TextInput source='livingCity' label="Residential City"/>
          <TextInput source='livingZipCode' label="Residential Postal Code"/>
          <SelectInput
            source="country"
            label="Residential Country"
            defaultValue="Österreich"
            choices={[
              { id: "Deutschland", name: "Deutschland" },
              { id: "Österreich", name: "Österreich" },
              { id: "Frankreich", name: "Frankreich" },
              { id: "Italien", name: "Italien" },
              { id: "Spanien", name: "Spanien" },
              { id: "Other", name: "Other" },
            ]}
          />

          <SelectInput
            label="Vehicle Ownership"
            source="bmwCarData.ownership"
            choices={[
              { id: "owner", name: "Owner" },
              { id: "sold", name: "Sold" },
            ]}
          />
          <NumberInput
            label='Current Mileage'
            source='bmwCarData.currentMileage'
          />
          <NumberInput
            label='Mileage on Sales Contract'
            source='bmwCarData.salesMileage'
          />
          <BooleanInput
            source='legalExpensesInsurance'
            label='Legal Expenses Insurance'
          />
        </FormTab>
        <FormTab label='RSV Info'>
          <BooleanInput
            source='legalExpensesInsurance'
            label='Legal Expenses Insurance'
          />
          <SelectInput
            source='insuranceProvider'
            choices={insuranceChoices}
          />
          <FormDataConsumer>
            {({ formData }) =>
              formData.insuranceProvider === "other" && (
                <TextInput
                  source='insuranceProviderManual'
                  label='Insurance Provider Name'
                  validate={(value: string) =>
                    !value || value.length < 3
                      ? "Minimum 3 characters required"
                      : undefined
                  }
                />
              )
            }
          </FormDataConsumer>
          <TextInput source='legalExpensesInsurancePolicyNumber' />
        </FormTab>

      </TabbedForm>
    </Edit>
  )
}
